import * as Sentry from '@sentry/react'
import React from 'react'
import { RouteFallback } from '@/shared/components/RouteFallback'
import { RouteErrorPage } from '@/shared/components/RouteErrorPage'
import type { ThemeSlug } from '@/themes/registry'

type ErrorBoundaryProps = {
  component: React.ComponentType<object>
  componentProps: object
  loadFallbackComponent: () => Promise<React.ComponentType<object>>
  sourcePath: string
  themeSlug: ThemeSlug
}

type ErrorBoundaryState = {
  fallbackDepth: 0 | 1 | 2
  fallbackComponent: React.ComponentType<object> | null
}

class ThemeErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { fallbackDepth: 0, fallbackComponent: null }

  public componentDidCatch(error: unknown): void {
    const nextDepth = this.state.fallbackDepth === 0 ? 1 : 2
    const eventName =
      nextDepth === 1 ? 'theme_render_fallback' : 'theme_render_fallback_exhausted'

    Sentry.captureException(error, {
      tags: {
        event: eventName,
        theme_slug: this.props.themeSlug,
        fallback_depth: String(nextDepth),
      },
      contexts: {
        theme: {
          source_path: this.props.sourcePath,
        },
      },
    })

    if (nextDepth === 1) {
      this.setState({ fallbackDepth: 1, fallbackComponent: null })
      void this.props.loadFallbackComponent()
        .then((fallbackComponent) => {
          this.setState({ fallbackComponent })
        })
        .catch((fallbackError: unknown) => {
          Sentry.captureException(fallbackError, {
            tags: {
              event: 'theme_render_fallback_exhausted',
              theme_slug: this.props.themeSlug,
              fallback_depth: '2',
            },
            contexts: {
              theme: {
                source_path: this.props.sourcePath,
              },
            },
          })
          this.setState({ fallbackDepth: 2, fallbackComponent: null })
        })
      return
    }

    this.setState({ fallbackDepth: 2, fallbackComponent: null })
  }

  public render(): React.ReactNode {
    if (this.state.fallbackDepth === 2) {
      return React.createElement(RouteErrorPage)
    }

    if (this.state.fallbackDepth === 1) {
      return this.state.fallbackComponent === null
        ? React.createElement(RouteFallback)
        : React.createElement(this.state.fallbackComponent, this.props.componentProps)
    }

    return React.createElement(this.props.component, this.props.componentProps)
  }
}

/**
 * Wraps a theme override so a render crash falls back to the default component once,
 * then to {@link RouteErrorPage} if the default also crashes (no infinite retry).
 */
export function withThemeErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  loadDefaultComponent: () => Promise<React.ComponentType<P>>,
  sourcePath: string,
  themeSlug: ThemeSlug,
): React.ComponentType<P> {
  function WrappedThemeComponent(props: P) {
    return React.createElement(ThemeErrorBoundary, {
      component: Component as React.ComponentType<object>,
      componentProps: props,
      loadFallbackComponent: loadDefaultComponent as () => Promise<React.ComponentType<object>>,
      sourcePath,
      themeSlug,
    })
  }

  WrappedThemeComponent.displayName = `Themed(${Component.displayName ?? Component.name ?? 'Component'})`

  return WrappedThemeComponent
}
